import { useLayoutEffect, useRef, useState } from 'react'
import { ArrowUpIcon } from '@heroicons/react/24/outline'
import AttachmentPicker from '../home/AttachmentPicker'
import ComposerAttachments from '../home/ComposerAttachments'
import { useComposerAttachments } from '../home/useComposerAttachments'
import PlanToggle from './PlanToggle'
import Spinner from './Spinner'
import { useChat } from '../../providers/chat/context'

const MAX_LENGTH = 8000
const MAX_HEIGHT = 288

function placeholderFor(locked: boolean, reason: 'permission' | 'summarization' | 'credits' | null) {
  if (!locked) return 'Ask a question or provide a task...'
  if (reason === 'permission') return 'Waiting for your decision above…'
  if (reason === 'summarization') return 'Pick a summarization option above to continue…'
  return 'Out of ad credits — top up to continue.'
}

export default function ChatComposer() {
  const { send, busy, locked, balance, pendingPermission, pendingSummarization, plans } = useChat()
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const outOfCredits = balance !== null && balance.credits_remaining <= 0
  const blocked = locked || outOfCredits
  const reason = pendingSummarization
    ? 'summarization'
    : pendingPermission
      ? 'permission'
      : outOfCredits
        ? 'credits'
        : null
  const uploads = useComposerAttachments(blocked)
  const running = plans.find((plan) => plan.status === 'running')
  const runningStep = running?.steps.find((step) => step.status === 'running')
  const overLimit = value.length > MAX_LENGTH
  const canSend =
    (value.trim().length > 0 || uploads.attachments.length > 0) &&
    !uploads.uploading &&
    !overLimit &&
    !blocked &&
    busy === null

  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_HEIGHT)}px`
  }, [value])

  function submit() {
    if (!canSend) return
    const message = value.trim()
    const sent = uploads.attachments
    setValue('')
    uploads.clear()
    void send(message, sent)
  }

  return (
    <div className="shrink-0 px-3 pb-3">
      {running && runningStep && (
        <div className="mx-auto mb-2 flex w-full max-w-3xl items-center gap-2 rounded-lg bg-stone-200 px-3 py-1.5 text-[11px] text-stone-700">
          <Spinner className="size-3 border-purple-200 border-t-purple-700" />
          <span className="shrink-0 font-medium">
            Step {runningStep.position} of {running.steps.length}
          </span>
          <span className="truncate text-stone-500">{runningStep.title}</span>
        </div>
      )}
      <div
        {...uploads.dropZone}
        className={`mx-auto w-full max-w-3xl rounded-2xl border border-stone-200 bg-white shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-16px_rgba(28,25,23,0.22)] transition focus-within:border-purple-400 ${
          uploads.dragging ? 'ring-2 ring-purple-400' : ''
        }`}
      >
        <ComposerAttachments uploads={uploads} compact />

        <textarea
          ref={textareaRef}
          value={value}
          rows={1}
          disabled={blocked}
          placeholder={placeholderFor(blocked, reason)}
          onChange={(event) => setValue(event.target.value)}
          onPaste={uploads.paste}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              submit()
            }
          }}
          className="block max-h-72 w-full resize-none overflow-y-auto bg-transparent px-4 pt-3.5 pb-1 text-sm leading-snug text-stone-900 [scrollbar-width:thin] placeholder:text-stone-400 focus:outline-none disabled:cursor-not-allowed"
        />

        <div className="flex items-center justify-between gap-2 px-2.5 pb-2.5">
          <div className="flex items-center gap-1">
            <AttachmentPicker disabled={blocked} uploads={uploads} />
            <PlanToggle disabled={blocked} />
          </div>

          <div className="flex items-center gap-2">
            {overLimit && (
              <span className="text-[11px] text-red-500">
                {value.length}/{MAX_LENGTH}
              </span>
            )}
            <button
              type="button"
              aria-label="Send message"
              disabled={!canSend}
              onClick={submit}
              className="flex size-7 items-center justify-center rounded-full bg-purple-700 text-white transition enabled:hover:bg-purple-800 disabled:bg-stone-100 disabled:text-stone-400"
            >
              <ArrowUpIcon className="size-3.5" strokeWidth={2.2} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
